// @ts-check

/** @file Offline task create/edit/delete against an open project replica. Each command writes the local domain rows in one replica transaction for immediate feedback, then enqueues a single replay intent on the conflict-tracked SyncClient so the coordinator can replay, rebase, and reconcile it when connectivity returns. */

/** @import {OpenProjectReplica} from "./project-replica.js" */

import Task from "../models/task.js"
import TaskBoard from "../models/task-board.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica.js"
import {syncUuid} from "./project-replica-mutation-log.js"

/**
 * Local attributes an offline task edit may change.
 * @typedef {object} OfflineTaskChanges
 * @property {string} [name] Replacement name.
 * @property {string | null} [description] Replacement description.
 * @property {number | null} [priority] Replacement priority.
 * @property {string | null} [taskTypeId] Replacement task type.
 */

/**
 * Reads the current authoritative updatedAt of a local record as an ISO string.
 * @param {InstanceType<typeof Task>} task Local task record.
 * @returns {string | null} The base version, or null when the record has none.
 */
function baseVersionForTask(task) {
  const value = task.updatedAt()

  if (value instanceof Date) return value.toISOString()
  if (typeof value === "string" || typeof value === "number") return String(value)

  return null
}

/**
 * Chooses the next local row number for a placement appended to a column.
 * The authoritative value lands later through the sync feed; this only needs
 * to be unique and sort last for immediate offline display.
 * @param {OpenProjectReplica} replica Replica whose column placement rows are read.
 * @param {string} boardColumnId Column receiving the appended placement.
 * @returns {Promise<number>} A value that sorts after every placement already in the column.
 */
async function nextPlacementRowNumber(replica, boardColumnId) {
  return await replica.handle.databaseOperation({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const rows = await operation.forModel(TaskBoard).where({boardColumnId}).toArray()
    const max = rows.reduce((highest, row) => Math.max(highest, Number(row.rowNumber() || 0)), 0)

    return max + 1
  })
}

/**
 * Queues an offline task create. Inserts the local Task and its initial
 * TaskBoard placement in one transaction, then enqueues a create-shaped intent
 * (no base version, syncType "update", boardColumnId present) that the
 * coordinator replays through the server's idempotent createTaskOnBoard command.
 * @param {object} args Create inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.boardId Destination board UUID.
 * @param {string} args.boardColumnId Destination column UUID.
 * @param {string} args.projectId Project UUID.
 * @param {string | null} args.userId Creator UUID.
 * @param {string} args.name Card title for the new task.
 * @param {string | null} args.description Long-form body for the new task, or null.
 * @param {number | null} args.priority Urgence level for the new task, or null.
 * @param {string | null} args.taskTypeId Task type UUID.
 * @returns {Promise<{taskId: string, task: InstanceType<typeof Task>, taskBoardId: string}>} The local task and placement identities.
 * @rejects {Error} When the replica is unusable.
 */
export async function queueOfflineTaskCreate({boardColumnId, boardId, description, name, priority, projectId, replica, taskTypeId, userId}) {
  const taskId = syncUuid()
  const taskBoardId = syncUuid()
  const now = new Date().toISOString()
  const rowNumber = await nextPlacementRowNumber(replica, boardColumnId)

  /** @type {Record<string, unknown>} */
  const taskAttributes = {
    createdAt: now,
    description: description ?? null,
    id: taskId,
    name,
    projectId,
    state: "open",
    updatedAt: now,
    userId: userId ?? null
  }

  if (priority != null && Number.isSafeInteger(priority)) taskAttributes.priority = priority
  if (taskTypeId) taskAttributes.taskTypeId = taskTypeId

  const task = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const createdTask = await operation.forModel(Task).create(taskAttributes)

    await operation.forModel(TaskBoard).create({
      boardColumnId,
      boardId,
      createdAt: now,
      id: taskBoardId,
      rowNumber,
      taskId,
      updatedAt: now
    })

    return createdTask
  })

  /** @type {Record<string, unknown>} */
  const payload = {
    boardColumnId,
    description: description ?? null,
    name,
    projectId,
    taskBoardId
  }

  if (priority != null && Number.isSafeInteger(priority)) payload.priority = priority
  if (taskTypeId) payload.taskTypeId = taskTypeId

  await replica.syncClient.queue({data: payload, operation: "create", resource: task})

  return {task, taskId, taskBoardId}
}

/**
 * Queues an offline task edit. Captures the record's base version, applies the
 * local change for immediate feedback, then enqueues a scalar update intent
 * chained behind any pending predecessor for the same task.
 * @param {object} args Edit inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.taskId Task UUID.
 * @param {OfflineTaskChanges} args.changes Local change to apply and replay.
 * @returns {Promise<InstanceType<typeof Task>>} The updated local task.
 * @rejects {Error} When the task is not present in the replica.
 */
export async function queueOfflineTaskEdit({changes, replica, taskId}) {
  const updated = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const task = await operation.forModel(Task).findBy({id: taskId})

    if (!task) throw new Error(`Project replica has no task ${taskId}`)

    const baseVersion = baseVersionForTask(task)

    if (changes.name !== undefined) task.setName(changes.name)
    if (changes.description !== undefined) task.setDescription(changes.description)
    if (changes.priority !== undefined) task.setPriority(changes.priority)
    if (changes.taskTypeId !== undefined) task.setTaskTypeId(changes.taskTypeId)

    await task.save()

    return {baseVersion, task}
  })

  /** @type {Record<string, unknown>} */
  const payload = {}

  if (changes.name !== undefined) payload.name = changes.name
  if (changes.description !== undefined) payload.description = changes.description
  if (changes.priority !== undefined) payload.priority = changes.priority
  if (changes.taskTypeId !== undefined) payload.taskTypeId = changes.taskTypeId

  await replica.syncClient.queue({baseVersion: updated.baseVersion, data: payload, operation: "update", resource: updated.task})

  return updated.task
}

/**
 * Queues an offline task delete. Destroys the local task and its placements for
 * immediate feedback, then enqueues a delete intent carrying the pre-delete base
 * version so a genuine remote change still surfaces as a conflict.
 * @param {object} args Delete inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.taskId Task UUID.
 * @returns {Promise<void>} Resolves after the intent is durable.
 * @rejects {Error} When the task is not present in the replica.
 */
export async function queueOfflineTaskDelete({replica, taskId}) {
  const {baseVersion, task} = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const task = await operation.forModel(Task).findBy({id: taskId})

    if (!task) throw new Error(`Project replica has no task ${taskId}`)

    const baseVersion = baseVersionForTask(task)
    const placements = await operation.forModel(TaskBoard).where({taskId}).toArray()

    for (const placement of placements) await placement.destroy()
    await task.destroy()

    return {baseVersion, task}
  })

  await replica.syncClient.queue({baseVersion, data: {id: taskId}, operation: "destroy", resource: task})
}
